import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {
  AUTH_MESSAGES,
  AuthProgressView,
  AuthSuccessView,
  SUCCESS_HOLD_MS,
  playExit,
  swapView,
  useSignInSteps,
  wait,
  withAuthBanner,
} from '~/src/components/auth/shell'
import {formatRoleName} from '~/src/utils/roleDisplay'
import {setSessionLoginTime} from '~/src/context/SessionTimeoutContext'
import {BASE_ROUTES} from '~/src/routes/constants'
import {useIopLoginMutation} from '~/src/store/apis/auth-api'
import {useLazyGetCurrentUserQuery} from '~/src/store/apis/user-api'
import {useLazyGetGaAgencyNamesAndIdsQuery} from '~/src/store/apis/member-access-api'
import {roleApi} from '~/src/services'
import {
  updateLoggedInAgencyId,
  updateLoggedInCompanyId,
  updateUserSignIn,
  updateCurrentUser,
  setBackendRolePermissions,
} from '~/src/store/slices/root-slice'
import {useAppDispatch} from '~/src/store/store-hooks'
import {isNullOrUndefined} from '~/src/utils'
import {getRouteForRole, type ModulePermission} from '~/src/utils/routeUtils'
import {
  noRolePermissions,
  resolveEffectiveModulePermissions,
} from '~/src/utils/effectiveModulePermissions'
import {consumeOAuthSession, getIopRedirectUri} from '~/src/utils/iop-auth'
import {consumePostSignInPath} from '~/src/utils/postSignInPath'
import type {IUserBase} from '~/src/models'
import {reportCatchError} from '~/src/utils/errorReporter'

export default function OAuthCallback() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [iopLogin] = useIopLoginMutation()
  const [getCurrentUser] = useLazyGetCurrentUserQuery()
  const [getGaAgencyNamesAndIds] = useLazyGetGaAgencyNamesAndIdsQuery()
  const [view, setView] = useState<'progress' | 'success'>('progress')
  const [roleLabel, setRoleLabel] = useState<string>()
  const {
    steps: stepList,
    start: startStep,
    complete: completeStep,
  } = useSignInSteps({withSession: true})

  const fetchAndStorePermissions = useCallback(
    async (user: IUserBase) => {
      if (user.roles.length > 1) {
        navigate(BASE_ROUTES.ROLE_SELECT)
        return undefined
      }

      const permissionsResponse = await roleApi.getPermissionsForRoles(user.roles as string[])
      let category = 'Platform'
      let modulePermissions: ModulePermission[] | undefined

      if (permissionsResponse.roles.length > 0) {
        const primaryRole = permissionsResponse.roles[0]
        if (primaryRole) {
          category = primaryRole.name
          // Permission resolution (resolveEffectiveModulePermissions, STFFLG-4243):
          // 1. Agency overrides are authoritative — STFFLG-2281 allows escalation
          // 2. User overrides are restrictive (AND) — per-user restrictions only tighten
          // A role with no tree gives no module permissions: the user's own trees never stand in.
          modulePermissions = resolveEffectiveModulePermissions({
            roleTree: primaryRole.modulePermissions as ModulePermission[] | undefined,
            agencyTree: user.agencyAssignments?.[0]?.modulePermissions as
              | ModulePermission[]
              | undefined,
            userTree: user.modulePermissions as ModulePermission[] | undefined,
          })

          dispatch(
            setBackendRolePermissions({
              _id: primaryRole.id,
              id: primaryRole.id,
              name: primaryRole.name,
              accessList: primaryRole.accessList,
              modulePermissions: modulePermissions,
              isPlatform: primaryRole.category === 'Platform',
              category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
              application: 'msp',
              createdDate: primaryRole.createdDate?.toString() ?? '',
              modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
              versionKey: Number(primaryRole.versionKey) || 0,
            }),
          )

          window.localStorage.setItem('cognitoRole', JSON.stringify({role: primaryRole}))
        }
      }
      // No role found for the user's role name: no module permissions. The user's own trees used
      // to be stored verbatim here, which made any grant saved above the role live (STFFLG-4243).
      // An explicit deny-everything role is stored (noRolePermissions): storing none would let the
      // route guard pass every route.
      if (!permissionsResponse.roles[0]) {
        dispatch(setBackendRolePermissions(noRolePermissions(user.roles[0])))
        window.localStorage.setItem(
          'cognitoRole',
          JSON.stringify({role: {id: user.roles[0], name: user.roles[0]}}),
        )
      }

      return {user, category, modulePermissions}
    },
    [dispatch, navigate],
  )

  const goToDashboard = useCallback(
    async (result?: {
      user: IUserBase
      category: string
      modulePermissions?: ModulePermission[]
    }) => {
      if (isNullOrUndefined(result)) return

      const {user, category, modulePermissions} = result
      setSessionLoginTime()
      dispatch(updateUserSignIn({data: true}))
      dispatch(updateLoggedInCompanyId(user.companyId?.length > 0 ? user.companyId : ''))

      const hasMultiAgencyAssignments = user.agencyAssignments && user.agencyAssignments.length > 0
      const isGeneralAdmin = user.roles?.includes('general_admin')

      if (hasMultiAgencyAssignments) {
        dispatch(updateLoggedInAgencyId(user.agencyAssignments![0].agencyId))
      } else if (isGeneralAdmin) {
        try {
          const agencies = await getGaAgencyNamesAndIds(user.id).unwrap()
          dispatch(updateLoggedInAgencyId(agencies.length > 0 ? agencies[0].id : ''))
        } catch (err) {
          reportCatchError('Failed to fetch agencies for general_admin:', err)
          dispatch(updateLoggedInAgencyId(''))
        }
      } else {
        dispatch(updateLoggedInAgencyId(user.agencyId?.length > 0 ? user.agencyId : ''))
      }

      // STFFLG-4163: back to the signing document whose link brought them here, if any
      // (rememberPostSignInPath in PrivateRoute), else their landing page.
      const targetRoute = consumePostSignInPath() ?? getRouteForRole(category, modulePermissions)

      completeStep('dashboard')
      setRoleLabel(formatRoleName(category))
      swapView(() => setView('success'))
      await wait(SUCCESS_HOLD_MS)
      await playExit()
      navigate(targetRoute)
    },
    [dispatch, navigate, getGaAgencyNamesAndIds, completeStep],
  )

  const showErrorAndRedirect = useCallback(
    (message: string) => {
      navigate(BASE_ROUTES.SIGNIN, {
        replace: true,
        state: withAuthBanner({tone: 'error', message}),
      })
    },
    [navigate],
  )

  const processedRef = useRef(false)

  useEffect(() => {
    async function handleCallback() {
      if (processedRef.current) return
      processedRef.current = true

      const code = searchParams.get('code')
      const returnedState = searchParams.get('state')
      const errorParam = searchParams.get('error')

      if (errorParam) {
        showErrorAndRedirect(AUTH_MESSAGES.ssoCancelled)
        return
      }

      if (!code) {
        showErrorAndRedirect('Missing authorization code from Single Sign')
        return
      }

      const session = consumeOAuthSession()
      if (!session) {
        showErrorAndRedirect(AUTH_MESSAGES.ssoExpired)
        return
      }

      if (returnedState !== session.state) {
        showErrorAndRedirect('OAuth state mismatch — possible CSRF attack. Please try again.')
        return
      }

      try {
        startStep('session')
        await iopLogin({
          code,
          redirectUri: getIopRedirectUri(),
          codeVerifier: session.verifier,
        }).unwrap()

        window.localStorage.setItem('isIopSession', 'true')
        completeStep('session')

        startStep('account')
        const user = await getCurrentUser().unwrap()
        dispatch(updateCurrentUser(user))
        completeStep('account')

        startStep('permissions')
        const permResult = await fetchAndStorePermissions(user)
        if (!permResult) return
        completeStep('permissions')

        startStep('dashboard')
        await goToDashboard(permResult)
      } catch (err: unknown) {
        const errObj = err as {data?: {message?: string}; message?: string}
        const rawMsg = errObj?.data?.message || errObj?.message || ''

        if (rawMsg.toLowerCase().includes('no') && rawMsg.toLowerCase().includes('account found')) {
          showErrorAndRedirect(AUTH_MESSAGES.noAccount)
        } else if (
          rawMsg.toLowerCase().includes('inactive') ||
          rawMsg.toLowerCase().includes('disabled')
        ) {
          showErrorAndRedirect(AUTH_MESSAGES.inactive)
        } else {
          showErrorAndRedirect(rawMsg || 'Sign-in with Single Sign failed. Please try again.')
        }
      }
    }

    void handleCallback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SignInContainer>
      {view === 'success' ? (
        <AuthSuccessView roleLabel={roleLabel} />
      ) : (
        <AuthProgressView steps={stepList} fromSso />
      )}
    </SignInContainer>
  )
}
